import type { PatientIdentity } from '@/features/patients/types';
export interface StaffRef {
  id: string;
  fullName: string;
}

export type PrescriptionStatus = 'draft' | 'signed' | 'cancelled';

export interface PrescriptionItem {
  id: string;
  medicine: string;
  dose: string;
  route: string | null;
  frequency: string;
  durationDays: number | null;
  quantity: number;
  unit: string | null;
  instructions: string | null;
  /** Draft only: allergy, dose, interaction and look-alike checks. */
  check?: {
    status: 'ok' | 'warning' | 'blocked';
    messages: { level: 'warning' | 'blocked'; text: string }[];
  } | null;
}

export interface PrescriptionItemInput {
  medicine: string;
  dose: string;
  route?: string;
  frequency: string;
  durationDays?: number;
  quantity: number;
  unit?: string;
  instructions?: string;
}

export interface Prescription {
  id: string;
  number: string | null;
  encounterId: string;
  status: PrescriptionStatus;
  prescriber: StaffRef;
  signedAt: string | null;
  cancelledAt: string | null;
  cancelledBy: StaffRef | null;
  cancelReason: string | null;
  version: number;
  items: PrescriptionItem[];
  blocked?: boolean;
}

export type LabOrderStatus =
  | 'draft'
  | 'ordered'
  | 'collected'
  | 'dispatched'
  | 'results_ready'
  | 'reviewed'
  | 'cancelled';

export type LabResultFlag = 'normal' | 'low' | 'high' | 'abnormal' | 'critical';

export const LAB_STATUS_LABEL: Record<LabOrderStatus, string> = {
  draft: 'Draft',
  ordered: 'Ordered',
  collected: 'Sample collected',
  dispatched: 'Dispatched to lab',
  results_ready: 'Results ready',
  reviewed: 'Reviewed',
  cancelled: 'Cancelled',
};

export const RESULT_FLAG_LABEL: Record<LabResultFlag, string> = {
  normal: 'Normal',
  low: 'Low',
  high: 'High',
  abnormal: 'Abnormal',
  critical: 'Critical',
};

export interface LabResult {
  value: string;
  unit: string | null;
  referenceRange: string | null;
  flag: LabResultFlag | null;
  note: string | null;
  resultedAt: string;
  resultedBy: StaffRef | null;
}

export interface LabOrder {
  id: string;
  number: string | null;
  encounterId: string;
  status: LabOrderStatus;
  clinicalNote: string | null;
  patient: {
    id: string;
    fullName: string;
    dateOfBirth: string | null;
    sex: 'male' | 'female' | null;
    identity: PatientIdentity | null;
  };
  branch: { id: string; code: string; name: string };
  /** App-package orders do not have an ordering clinician. */
  orderedBy: StaffRef | null;
  /**
   * API RL-1840 (GET results/to-review only): held for a doctor who has left
   * (contract ended, access ended or no longer a doctor); any branch doctor
   * may review and release it.
   */
  takeover?: boolean;
  orderedAt: string | null;
  collectedAt: string | null;
  collectedBy: StaffRef | null;
  dispatchedAt: string | null;
  resultsReadyAt: string | null;
  reviewedAt: string | null;
  reviewedBy: StaffRef | null;
  reviewComment: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  collectionMode: 'clinic' | 'home';
  homeVisitAt: string | null;
  homeVisitPlace: string | null;
  labelsPrintedAt: string | null;
  version: number;
  tests: LabTestLine[];
}

/** Per-test doctor review (RL-724): released to the app, held, or waiting. */
export type ResultReview = 'released' | 'held' | 'to_review' | null;

export const REVIEW_LABEL: Record<Exclude<ResultReview, null>, string> = {
  released: 'Released',
  held: 'Held · call patient',
  to_review: 'To review',
};

export interface LabTestLine {
  id: string;
  testName: string;
  tube: string | null;
  resultSource: 'manual' | 'feed' | 'paper' | null;
  review: ResultReview;
  releasedAt: string | null;
  releasedBy: StaffRef | null;
  heldAt: string | null;
  heldBy: StaffRef | null;
  doctorComment: string | null;
  callNote: string | null;
  result: LabResult | null;
}

export interface LabDashboard {
  tiles: {
    toCollectToday: number;
    atTheLab: number;
    resultsInToday: number;
    homeVisits: number;
    unmatchedResults: number;
  };
  pipeline: {
    booked: number;
    collected: number;
    atTheLab: number;
    toReview: number;
    releasedToday: number;
  };
  collectionList: LabOrder[];
}

export interface SpecimenLabel {
  labelId: string;
  tube: string;
  tests: string[];
  patientName: string;
  icLast4: string | null;
  dateOfBirth: string | null;
  takenAt: string;
  collectorInitials: string;
  orderNumber: string | null;
  qr: string;
}

export interface FeedObservation {
  test: string;
  value: string;
  unit: string | null;
  referenceRange: string | null;
  flag: LabResultFlag | null;
}

export interface FeedMessage {
  id: string;
  controlId: string;
  status: 'imported' | 'unmatched' | 'rejected';
  simulated: boolean;
  receivedAt: string;
  importedAt: string | null;
  importedBy: StaffRef | null;
  orderNumber: string | null;
  labOrder: { id: string; number: string | null } | null;
  identityLast4: string | null;
  patientName: string | null;
  sendingLab: string | null;
  observations: FeedObservation[];
}

export interface InboxResult {
  itemId: string;
  labOrderId: string;
  orderNumber: string | null;
  orderStatus: LabOrderStatus;
  patient: { id: string; fullName: string };
  testName: string;
  value: string | null;
  unit: string | null;
  referenceRange: string | null;
  flag: LabResultFlag | null;
  source: 'manual' | 'feed' | 'paper' | null;
  resultedAt: string;
  routedTo: StaffRef;
  review: ResultReview;
}

export interface ResultsInbox {
  feed: {
    protocol: string;
    connection: 'simulated' | 'connected';
    lab: string;
    lastMessageAt: string | null;
    unmatched: number;
  };
  messages: FeedMessage[];
  results: InboxResult[];
}

export type ImagingStatus =
  'referred' | 'appointment_made' | 'report_in' | 'reviewed';

export const IMAGING_STATUS_LABEL: Record<ImagingStatus, string> = {
  referred: 'Referred',
  appointment_made: 'Appointment made',
  report_in: 'Report in, for the doctor',
  reviewed: 'Reviewed',
};

export interface ImagingReferral {
  documentId: string;
  number: string;
  encounterId: string;
  patient: { id: string; fullName: string; identity: string | null };
  exam: string | null;
  referredTo: string | null;
  reason: string | null;
  referredAt: string;
  doctor: StaffRef;
  status: ImagingStatus;
  appointmentAt: string | null;
  appointmentNote: string | null;
  reportText: string | null;
  reportAt: string | null;
  reviewedAt: string | null;
  reviewComment: string | null;
}

export interface CatalogueTest {
  id: string;
  code: string;
  name: string;
  tube: string;
  unit: string | null;
  referenceRange: string | null;
  priceSen: number | null;
  memberPriceSen: number | null;
  panelCovered: boolean;
  isActive: boolean;
}

export interface OutsideRow {
  test: string;
  value: string;
  unit: string | null;
  referenceRange: string | null;
  flag: LabResultFlag | null;
}

export interface OutsideReport {
  id: string;
  patientId: string;
  source: string | null;
  reportDate: string | null;
  results: OutsideRow[];
  enteredAt: string;
  enteredBy: StaffRef;
  patient?: { id: string; fullName: string };
}

export interface TrendPoint {
  at: string;
  value: string;
  unit: string | null;
  referenceRange: string | null;
  flag: LabResultFlag | null;
  doctorComment: string | null;
  review: ResultReview;
  source: 'clinic' | 'outside';
  orderNumber: string | null;
}

export interface LabResultInput {
  value: string;
  unit?: string;
  referenceRange?: string;
  flag?: LabResultFlag;
  note?: string;
  /** API RL-1842: required with `flag: 'critical'` on a typed result. */
  confirmCritical?: boolean;
}
